import React, { useCallback, useEffect } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import to from 'await-to-js';
import { Alert, Button, DialogV2 } from 'c-components';
import apiClient from 'c-data/apiClient';
import { useAPIClientRequest } from 'c-hooks';
import { useCommonTranslation } from 'c-translation';

type Props = {
    campaignId: number;
    venueCount: number;
    open: boolean;
    onClose: () => void;
    onDeleted: () => void;
};

/**
 * UL-7957 -- clear the campaign's allowed venue list, after saying plainly what that means.
 *
 * No list means every screen is allowed, so nothing can be flagged unbooked any more: delivery on a
 * screen that was never bought will count as booked. The API soft-deletes the rows, as an upload
 * supersedes them.
 */
const AllowedVenueDeleteDialog: React.FC<Props> = ({
    campaignId,
    venueCount,
    open,
    onClose,
    onDeleted,
}) => {
    const t = useCommonTranslation();
    const { start, reset, isLoading, error } = useAPIClientRequest(
        apiClient.Entities.Campaign.deleteAllowedVenues,
    );

    useEffect(() => {
        if (open) reset();
    }, [open, reset]);

    const onConfirm = useCallback(async () => {
        const [err] = await to(start(campaignId));
        if (err) return;

        onDeleted();
        onClose();
    }, [campaignId, onClose, onDeleted, start]);

    return (
        <DialogV2
            open={open}
            onClose={onClose}
            maxWidth="sm"
            title="Modules.Main.Campaigns.CampaignSchedule.dsp.venues.delete.title"
        >
            <Stack gap={2}>
                <Alert severity="warning" variant="outlined">
                    {t('Modules.Main.Campaigns.CampaignSchedule.dsp.venues.delete.warning')}
                </Alert>
                <Typography variant="body2">
                    {t('Modules.Main.Campaigns.CampaignSchedule.dsp.venues.delete.count', {
                        count: venueCount,
                    })}
                </Typography>
                {error && (
                    <Alert severity="error" variant="outlined">
                        {String(error)}
                    </Alert>
                )}
                <Stack direction="row" justifyContent="flex-end" gap={1}>
                    <Button variant="text" onClick={onClose} disabled={isLoading}>
                        {t('Modules.Main.Campaigns.CampaignSchedule.dsp.venues.delete.cancel')}
                    </Button>
                    <Button color="error" onClick={onConfirm} disabled={isLoading}>
                        {isLoading ? (
                            <CircularProgress size={20} />
                        ) : (
                            t('Modules.Main.Campaigns.CampaignSchedule.dsp.venues.delete.confirm')
                        )}
                    </Button>
                </Stack>
            </Stack>
        </DialogV2>
    );
};

export default AllowedVenueDeleteDialog;
